import type { I18n, MessageDescriptor } from '@lingui/core'
import { isLocale, locales, type Locale } from 'shared/locales'
import { isSeoPath, localePath } from './locale-path'

export const SITE_NAME = 'D’Anchiano'

/** The only social profile the site publishes (the landing footer links it); `sameAs` may claim no other. */
export const LINKEDIN_URL = "https://linkedin.com/company/d'anchiano"

// The canonical host, never the one that answered: a preview or staging deploy must not self-canonicalise, or
// Google indexes it instead of production.
const SITE_URL = 'https://danchiano.com'

// The shared 1200x630 link-preview card. Locale-neutral on purpose (wordmark only), so one asset serves all four.
const DEFAULT_CARD = `${SITE_URL}/images/og-image.png`

// Google truncates the snippet around 160 characters, so the tag is cut at a word rather than mid-sentence.
const DESCRIPTION_LIMIT = 160

function clampDescription(text: string): string {
  if (text.length <= DESCRIPTION_LIMIT) return text
  const cut = text.slice(0, DESCRIPTION_LIMIT)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

const ogLocale = (locale: Locale) => locale.replace('-', '_')

/**
 * The hreflang set: one URL per published locale, each a locale-prefixed PATH rather than a `?lang=` parameter —
 * the stronger signal, and the only one that lets each alternate be self-canonical (see `canonicalUrl`).
 *
 * `x-default` is the UNPREFIXED path, which is the one legacy inbound links and the transactional emails point at
 * and the only URL that still resolves the visitor's own language from the cookie/`Accept-Language`. That is
 * exactly what `x-default` is for, and it canonicalises into whichever prefixed URL it served.
 */
function alternates(path: string) {
  // A page outside the prefixed set (the auth screens) has ONE URL serving every language, so `localePath` hands
  // back the same href four times — a set of four identical alternates, which states nothing. It gets none, and
  // canonicalises to itself below.
  if (!isSeoPath(path)) return []
  return [
    ...locales.map((locale) => ({
      rel: 'alternate',
      hreflang: locale,
      href: `${SITE_URL}${localePath(path, locale)}`,
    })),
    { rel: 'alternate', hreflang: 'x-default', href: `${SITE_URL}${path}` },
  ]
}

// Always the prefixed URL, including on the unprefixed one: a bare path serves a language chosen per visitor, so
// self-canonicalising it would put a second indexable copy of every page beside its own alternate.
const canonicalUrl = (path: string, locale: Locale) => `${SITE_URL}${localePath(path, locale)}`

/** The head-option context every route's `head:` receives; only the one field the tags are built from. */
type SeoContext = {
  match: { context: { i18n: I18n } }
}

type PageCopy = {
  /** The page's own half of the title; `SITE_NAME` is appended. */
  title: MessageDescriptor
  description: MessageDescriptor
  /** An absolute URL, overriding the shared brand card. `null` ships no card image and no `twitter:card`. */
  image?: string | null
}

export type PageSeo =
  | (PageCopy & {
      /** The path this page is served at, with no locale and no trailing slash ('/' for the landing). */
      path: string
      /** A page that exists but should not be indexed — a thin flow step, a placeholder. */
      noindex?: boolean
      /** schema.org blocks for this page, one `application/ld+json` script each, built from the resolved copy. */
      jsonLd?: (resolved: {
        title: string
        description: string
        url: string
        /** The request's own translator, so markup built from rendered copy resolves to the language it is served in. */
        translate: (message: MessageDescriptor) => string
      }) => Record<string, unknown>[]
    })
  // A token link is one recipient's URL, not a page: it can state no canonical, no alternates and no structured
  // data, and the missing `path` is only allowed here because the variant is `noindex` by type.
  | (PageCopy & { path?: never; noindex: true })

/**
 * A public page's whole head: title, description, canonical, hreflang, Open Graph, Twitter and JSON-LD.
 *
 * Copy arrives as Lingui descriptors and is translated HERE, off the request-scoped `i18n` on the root context,
 * so a page declares only what is its own. The active locale is read off that instance rather than the router
 * context: both agree at SSR, and it is the one that stays right after a client-side language switch.
 */
export function pageSeo(ctx: SeoContext, page: PageSeo) {
  const { i18n } = ctx.match.context
  const locale: Locale = isLocale(i18n.locale) ? i18n.locale : 'es-ES'
  const title = `${i18n._(page.title)} | ${SITE_NAME}`
  const description = clampDescription(i18n._(page.description))
  const cardImage = page.image === undefined ? DEFAULT_CARD : page.image
  const addressed =
    page.path === undefined ? undefined : { path: page.path, url: canonicalUrl(page.path, locale), jsonLd: page.jsonLd }
  const translate = (message: MessageDescriptor) => i18n._(message)
  const schemas = addressed ? (addressed.jsonLd?.({ title, description, url: addressed.url, translate }) ?? []) : []

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      // Stated either way, never omitted: the `_app` shell declares `noindex` for the whole private area, and the
      // router keeps ONE meta per name — so a door into the product only stays indexable by saying so here.
      { name: 'robots', content: page.noindex ? 'noindex, nofollow' : 'index, follow' },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      ...(addressed ? [{ property: 'og:url', content: addressed.url }] : []),
      // No `og:locale:alternate`: the router keys meta by `name`/`property` and keeps ONE tag per key, so the
      // set would collapse to whichever locale came last. The hreflang links carry the same set, losslessly.
      { property: 'og:locale', content: ogLocale(locale) },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      ...(cardImage
        ? [
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:image', content: cardImage },
            { name: 'twitter:image:alt', content: title },
            { property: 'og:image', content: cardImage },
            { property: 'og:image:alt', content: title },
            // Only for the card this module owns: an overriding page hands in a URL whose size and type it never states.
            ...(cardImage === DEFAULT_CARD
              ? [
                  { property: 'og:image:width', content: '1200' },
                  { property: 'og:image:height', content: '630' },
                  { property: 'og:image:type', content: 'image/png' },
                ]
              : []),
          ]
        : []),
    ],
    links: addressed ? [{ rel: 'canonical', href: addressed.url }, ...alternates(addressed.path)] : [],
    scripts: schemas.map((schema) => ({ type: 'application/ld+json', children: JSON.stringify(schema) })),
  }
}

/**
 * The organisation behind every page, declared once on the landing so a knowledge panel has one source.
 *
 * The two nodes carry `@id`s and the site names the organisation as its `publisher`, so they read as one graph
 * rather than two entities that happen to share a name. `sameAs` lists only the profile the footer links.
 */
export function organizationJsonLd(description: string): Record<string, unknown>[] {
  const organizationId = `${SITE_URL}/#organization`
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': organizationId,
      'name': SITE_NAME,
      'legalName': 'KNOWLEDGE TO DECIDE K2D, S.L.',
      'url': SITE_URL,
      'logo': `${SITE_URL}/images/logo.png`,
      'description': description,
      'sameAs': [LINKEDIN_URL],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      'name': SITE_NAME,
      'url': SITE_URL,
      // The same constant the hreflang set is built from, so it cannot claim a language the site does not serve.
      'inLanguage': [...locales],
      'publisher': { '@id': organizationId },
    },
  ]
}

/**
 * The questions a page renders, as a `FAQPage`. Takes the page's own descriptors and the request's translator, so
 * the markup is the SAME array the accordion renders, resolved into the SAME language — a rich result cannot
 * answer a question the visitor's page does not show.
 */
export function faqPageJsonLd(
  translate: (message: MessageDescriptor) => string,
  entries: readonly { question: MessageDescriptor; answer: MessageDescriptor }[],
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': entries.map((entry) => ({
      '@type': 'Question',
      'name': translate(entry.question),
      'acceptedAnswer': { '@type': 'Answer', 'text': translate(entry.answer) },
    })),
  }
}
